import { useState } from 'react';
import { ThemeProvider } from 'styled-components';
import PropTypes from 'prop-types';

import theme from 'styles/theme';
import darkTheme from 'styles/darkTheme';
import GlobalStyles from 'styles/globalStyles';
import useColorScheme from 'hooks/useColorScheme';

import { ensureMinContrast } from 'helpers/contrast.helper';

import AppContext from './context';

const DEFAULT_COLORS = {
  main: '#3AC09D',
  font: '#1F2D5C',
  background: '#F3F6FA',
};

const getColors = (colors = DEFAULT_COLORS) => {
  const { main, font, background } = colors || {};

  return {
    main: main || DEFAULT_COLORS.main,
    font: font || DEFAULT_COLORS.font,
    background: background || DEFAULT_COLORS.background,
  };
};

export default function AppContextProvider({ children }) {
  const [appData, setAppData] = useState({});
  const [colorTheme, setColorTheme] = useState(DEFAULT_COLORS);
  const [isCompactEntity, setIsCompactEntity] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSidebarShown, setSidebarShow] = useState(false);
  const {
    scheme, setScheme, resolved, isDark,
  } = useColorScheme();

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const values = {
    portal: appData?.portal,
    user: appData?.customer,
    stripeAccountId: appData?.stripeAccountId,
    isServicePlansAvailable: appData?.isServicePlansAvailable,
    isCardPaymentsAvailable: appData?.isCardPaymentsAvailable,
    pendingEstimates: appData?.pendingEstimates,
    country: appData?.country,
    setAppData,
    setColorTheme,
    colorTheme,
    appointment: {
      isCompact: isCompactEntity,
      toggleCompact: () => setIsCompactEntity(!isCompactEntity),
    },
    sidebar: {
      isShow: isSidebarShown,
      toggle: () => setSidebarShow(!isSidebarShown),
      hide: () => setSidebarShow(false),
    },
    isPreviewMode,
    setIsPreviewMode,
    appearance: {
      scheme,
      setScheme,
      resolved,
      isDark,
    },
  };

  const baseTheme = isDark ? darkTheme : theme;
  const companyColors = getColors(colorTheme);

  const appTheme = {
    ...baseTheme,
    isDark,
    companyColors: isDark
      ? {
        ...companyColors,
        // A company's own brand colour can be a dark navy or near-black - readable as an accent on
        // the light page it was chosen against, but indistinguishable from the dark theme's own
        // background once that background is dark too. Lightened only if it actually needs it.
        main: ensureMinContrast(companyColors.main, baseTheme.defaultColors.background),
        background: baseTheme.defaultColors.background,
        font: baseTheme.defaultColors.dark,
      }
      : companyColors,
  };

  return (
    <AppContext.Provider value={values}>
      <ThemeProvider theme={appTheme}>
        <GlobalStyles />

        {children}
      </ThemeProvider>
    </AppContext.Provider>
  );
}

AppContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
